import { FC, useCallback, useMemo } from 'react'
import { Form, Formik } from 'formik'
import * as Yup from 'yup'
import { useDispatch, useSelector } from 'react-redux'

import { Info } from '@src/components/PersonalInfo/Info'
import style from '@src/pages/RecoveryPassword/RecoveryPassword.module.scss'
import { Button } from '@src/components/Button/Button'
import { FieldFormik } from '@src/components/common/FieldFormik/FieldFormik'
import { AuthFormEnum } from '@src/types/auth'
import { validateForm } from '@src/messages/validateForm'
import { phoneRegexp } from '@src/lib/regex'
import { isString } from '@src/lib/utils'
import { recallPassword, recallPasswordByPassport } from '@src/store/ducks/auth/thunks'
import { AppDispatch } from '@src/store/store'
import { selectBackError, selectIsLoading, selectIsRecallPasswordNoUser } from '@src/store/ducks/auth/selectors'
import useQuery from '@src/hooks/useQuery'

enum TypeRecoveryContract {
  PHONE = 1,
  PASSPORT = 2,
}

const validationSchema = Yup.object().shape({
  account: Yup.string().when('type', {
    is: AuthFormEnum.PHONE,
    then: Yup.string().matches(phoneRegexp, validateForm.phone).required(validateForm.required),
    otherwise: Yup.string().required(validateForm.required),
  }),
  phone: Yup.string().when('type', {
    is: AuthFormEnum.CONTRACT,
    then: Yup.string().when('typeRecoveryContract', {
      is: TypeRecoveryContract.PHONE,
      then: Yup.string().matches(phoneRegexp, validateForm.phone).required(validateForm.required),
    }),
  }),
  passport: Yup.string().when('type', {
    is: AuthFormEnum.CONTRACT,
    then: Yup.string().when('typeRecoveryContract', {
      is: TypeRecoveryContract.PASSPORT,
      then: Yup.string().max(20, 'Максимальная длинна личного номера 20 символов').required(validateForm.required),
    }),
  }),
})

type RecoveryFormProps = {
  type: AuthFormEnum
}

export const RecoveryForm: FC<RecoveryFormProps> = ({ type }) => {
  const dispatch = useDispatch<AppDispatch>()
  const isLoading = useSelector(selectIsLoading)
  const backError = useSelector(selectBackError)
  const isRecallPasswordNoUser = useSelector(selectIsRecallPasswordNoUser)
  const isTv = useQuery('tv')

  const initialValues = useMemo(() => ({
    type,
    account: '',
    phone: '',
    passport: '',
    typeRecoveryContract: TypeRecoveryContract.PHONE as TypeRecoveryContract,
  }), [type])

  const handleSubmit = useCallback((values) => {
    if (values.type === AuthFormEnum.CONTRACT) {
      if (values.typeRecoveryContract === TypeRecoveryContract.PHONE) {
        dispatch(recallPassword(values.account, values.phone))
      } else {
        dispatch(recallPasswordByPassport(values.account, values.passport))
      }
    } else {
      dispatch(recallPassword(values.account.replace(/\D+/g, ''), values.account))
    }
  }, [dispatch])

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
      enableReinitialize
    >
      {({ values, setFieldValue }) => (
        <Form>
          <div className={style.box_1}>
            <FieldFormik
              name="account"
              placeholder={type === AuthFormEnum.CONTRACT ? 'ХХХХХХХХХХХХХХ' : '+375 (ХХ) ХХХ ХХ ХХ'}
              mask={type === AuthFormEnum.PHONE ? '+{375} (00) 000-00-00' : undefined}
              label={type === AuthFormEnum.CONTRACT ? 'Введите ваш номер договора' : 'Введите ваш номер телефона'}
            />

            {type === AuthFormEnum.CONTRACT && values.typeRecoveryContract === TypeRecoveryContract.PHONE && (
              <FieldFormik
                name="phone"
                placeholder="+375 (ХХ) ХХХ ХХ ХХ"
                mask="+{375} (00) 000-00-00"
                label="Введите ваш номер телефона"
              />
            )}
            {type === AuthFormEnum.CONTRACT && values.typeRecoveryContract === TypeRecoveryContract.PASSPORT && (
              <FieldFormik
                name="passport"
                placeholder="Личный номер"
                label="Личный номер в паспорте"
              />
            )}
          </div>

          {backError && isString(backError) && <div className={style.error}>{backError}</div>}

          {isRecallPasswordNoUser && (
            <button
              className={style.btnRecovery}
              type="button"
             // eslint-disable-next-line max-len
              onClick={() => { setFieldValue('typeRecoveryContract', values.typeRecoveryContract === TypeRecoveryContract.PHONE ? TypeRecoveryContract.PASSPORT : TypeRecoveryContract.PHONE) }}
            >
              {
                values.typeRecoveryContract === TypeRecoveryContract.PHONE
                  ? 'Восстановить пароль с помощью паспортных данных'
                  : 'Восстановить пароль с помощью мобильного телефона'
              }
            </button>
          )}

          <div className={`${style.btn}`}>
            {!isTv && (
              <Button
                type="text"
                outline
                className={style.btnSize}
                // @ts-ignore
                onClickFunc={() => { window?.Tawk_API?.maximize() }}
              >
                Написать в чат
              </Button>
            )}

            <Button
              loading={isLoading}
              type="text"
              outline
              className={style.btnSize}
              htmlType="submit"
            >
              Отправить
            </Button>
          </div>
          <Info name="Отправить" />
        </Form>
      )}
    </Formik>

  )
}
